/*
  cfw-cart-drawer.js  (COLLECTIONS agent, 2026-09-16)

  Bridge between custom add-to-cart code and Horizon's native cart drawer
  (<cart-drawer-component>, rendered by snippets/header-actions.liquid when
  settings.cart_type == 'drawer'). The drawer's CONTENT comes from
  snippets/cart-products.liquid and snippets/cart-summary.liquid.

  Public API (integration contract #3):
    window.CFW.openCartDrawer(cartJson?)   open the drawer; if cartJson is
                                           given, the drawer re-renders first
    window.CFW.cartDrawerError(message)    open the drawer and show an error
    document.dispatchEvent(new CustomEvent('cfw:cart:added', {detail:{item, cart}}))
    document.dispatchEvent(new CustomEvent('cfw:cart:error', {detail:{message}}))

  Also handles the "Complete the fit" add buttons ([data-cfw-add-variant]).
*/
import { CartAddEvent, ThemeEvents } from '@theme/events';

const CFW = (window.CFW = window.CFW || {});
let lastTrigger = null;
let pendingError = '';

const getDrawer = () => document.querySelector('cart-drawer-component');
const getDialog = () => getDrawer()?.querySelector('dialog');

function announce(message) {
  let region = document.getElementById('cfw-cart-live');
  if (!region) {
    region = document.createElement('div');
    region.id = 'cfw-cart-live';
    region.className = 'visually-hidden';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    document.body.appendChild(region);
  }
  region.textContent = '';
  window.setTimeout(() => {
    region.textContent = message;
  }, 50);
}

function renderError() {
  const dialog = getDialog();
  if (!dialog) return;
  let box = dialog.querySelector('.cfw-cd-error');
  if (!pendingError) {
    if (box) box.hidden = true;
    return;
  }
  if (!box) {
    box = document.createElement('p');
    box.className = 'cfw-cd-error';
    box.setAttribute('role', 'alert');
    const anchor = dialog.querySelector('.cart-drawer__content') || dialog.querySelector('.cart-drawer__inner') || dialog;
    anchor.prepend(box);
  }
  box.textContent = pendingError;
  box.hidden = false;
}

function watchClose(drawer) {
  const dialog = drawer.querySelector('dialog');
  if (!dialog || dialog.dataset.cfwWatched) return;
  dialog.dataset.cfwWatched = '1';
  dialog.addEventListener('close', () => {
    drawer.removeAttribute('data-cfw-added');
    document.body.classList.remove('cfw-drawer-open');
    pendingError = '';
    const target = lastTrigger;
    lastTrigger = null;
    if (target && document.contains(target) && typeof target.focus === 'function') {
      target.focus({ preventScroll: true });
    }
  });
}

function showDrawer({ added = false } = {}) {
  const drawer = getDrawer();
  if (!drawer) return false;
  watchClose(drawer);
  if (added) drawer.setAttribute('data-cfw-added', '');
  const dialog = drawer.querySelector('dialog');
  if (!lastTrigger && document.activeElement && document.activeElement !== document.body) {
    lastTrigger = document.activeElement;
  }
  if (!dialog || !dialog.open) {
    if (typeof drawer.open === 'function') drawer.open();
    else if (typeof drawer.showDialog === 'function') drawer.showDialog();
    // Horizon's cart-drawer-component.open() is a no-op in some states (it
    // relies on internal refs that are not wired on every template), which
    // left the drawer populated but never shown. Fall back to the native
    // dialog so an add-to-cart always ends in a visible drawer.
    if (dialog && !dialog.open) {
      try {
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      } catch (e) {
        try { dialog.setAttribute('open', ''); } catch (e2) { return false; }
      }
    }
  }
  document.body.classList.add('cfw-drawer-open');
  window.setTimeout(renderError, 60);
  return true;
}

function refreshWith(cart) {
  if (!cart) return;
  document.dispatchEvent(
    new CartAddEvent(cart, 'cfw-cart-drawer', {
      source: 'cfw-cart-drawer',
      itemCount: cart.item_count,
    })
  );
}

CFW.openCartDrawer = function (cart, opts = {}) {
  const added = opts.added !== undefined ? opts.added : Boolean(cart);
  if (!getDrawer()) {
    // Cart page (no drawer there) or drawer disabled: go to / reload the cart.
    if (window.location.pathname.replace(/\/$/, '').endsWith('/cart')) window.location.reload();
    else window.location.href = (window.Shopify?.routes?.root || '/') + 'cart';
    return;
  }
  if (cart) refreshWith(cart);
  showDrawer({ added });
  if (added) announce('Added to your cart');
};

CFW.cartDrawerError = function (message) {
  pendingError = message || 'Sorry, that could not be added. Please call us at (704) 846-0301.';
  if (!showDrawer({ added: false })) {
    window.alert(pendingError);
    return;
  }
  renderError();
  announce(pendingError);
};

document.addEventListener('cfw:cart:added', (event) => {
  const detail = event.detail || {};
  pendingError = '';
  CFW.openCartDrawer(detail.cart || null, { added: true });
});

document.addEventListener('cfw:cart:error', (event) => {
  CFW.cartDrawerError(event.detail?.message || event.detail?.description);
});

// Horizon's own add-to-cart (quick add, product forms) also opens the drawer
// with the "Added" header. Ignore quantity changes made inside the cart.
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const data = event.detail?.data || {};
  if (data.source === 'cart-items-component' || data.source === 'cfw-cart-drawer' || data.didError) return;
  if (!(event instanceof CartAddEvent)) return;
  const drawer = getDrawer();
  if (drawer) {
    watchClose(drawer);
    drawer.setAttribute('data-cfw-added', '');
  }
});

// Re-apply the error box after the drawer re-renders.
document.addEventListener(ThemeEvents.cartUpdate, () => window.setTimeout(renderError, 400));

/* ---------- Complete the fit: one-tap add ---------- */
async function addVariant(button) {
  const id = Number(button.dataset.cfwAddVariant);
  if (!id || button.disabled) return;
  const label = button.innerHTML;
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  lastTrigger = getDrawer() ? null : button;
  try {
    const res = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
    });
    const body = await res.json();
    if (!res.ok || body.status) {
      CFW.cartDrawerError(body.description || body.message);
      return;
    }
    const cart = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js', {
      headers: { Accept: 'application/json' },
    }).then((r) => r.json());
    document.dispatchEvent(
      new CustomEvent('cfw:cart:added', { detail: { item: body.items ? body.items[0] : body, cart } })
    );
  } catch (e) {
    CFW.cartDrawerError();
  } finally {
    button.disabled = false;
    button.removeAttribute('aria-busy');
    button.innerHTML = label;
  }
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const button = target?.closest('[data-cfw-add-variant]');
  if (button) {
    event.preventDefault();
    addVariant(button);
  }
});
